// FeedbackFlow's copy, in one place so the component and its suite reference
// the same string instead of each holding a copy-pasted copy of it.
//
// The therapist name is a runtime slot, so lines carrying it are FUNCTIONS. No
// line OPENS with the slot: the "your therapist" fallback is lowercase and
// cannot start a sentence (G8).

import type { ExpectedOutcome } from "../../expectedOutcome";

/** Used when the couple has no therapist name loaded. Lowercase by design (G8). */
export const FALLBACK_THERAPIST_NAME = "your therapist";

import { BACK_TO_HOME } from "../FlowOutcomeScreen";

/** The one way out of every terminal feedback screen (shared with the check-in). */
export { BACK_TO_HOME };

/**
 * The EXPECTED outcome behind a CLOSED feedback window — a business answer, not a failure.
 *
 * Both routes into it share this copy — the `window_open: false` the GET reports, and the 409 the
 * PUT raises — because they are one state, not two. It renders in every environment, never
 * through `FriendlyError`: see `ExpectedOutcome`.
 *
 * 🔴 IT MUST NOT NAME A CAUSE, because the read does not give one.
 * `feedback_window_open` (`services/session_reflections.py:41-45`) is
 * `status != CANCELED and (now >= end_utc or status == COMPLETED)`, so a false collapses TWO
 * different situations into one boolean: a session that has not finished yet, and a session that
 * was canceled (false forever, however long ago it ended). The wire carries no way to tell them
 * apart — `FeedbackStateOut` has `window_open` and the feedback row, and nothing else.
 *
 * An earlier draft said "This session hasn't finished yet" and promised a next-day message. On a
 * canceled session both halves are simply false, and the second promises a nudge that will never
 * be sent — a confident claim from a read that never made it. Making the promise conditional on
 * TIME ("if your session is still ahead of you") does not fix it either: a session canceled
 * BEFORE its start time satisfies that condition and still gets no nudge.
 *
 * So the lines below state the RULE and nothing else, and the promise is conditional on the
 * session TAKING PLACE — the same condition the reminder job itself runs on. Naming which
 * situation a given client is in needs the server to say so; that is an API change, not a copy
 * edit.
 */
export const FEEDBACK_CLOSED: ExpectedOutcome = {
  title: "Feedback isn’t open for this session",
  body: "It opens once a session has finished, and stays closed for a session that was canceled.",
  // Still says what the product will do next, so a wait is a plan rather than a dead end. The
  // nudge is real: `cronjobs/reflection_reminder_copy.py`'s FEEDBACK_DAY_AFTER message deeplinks
  // at this exact flow the day after the session.
  nextStep: (therapistName: string) =>
    `Nothing has been sent. Once a session has taken place, we’ll message you the day after it with a link back to this page — reach out to ${therapistName} if something can’t wait.`,
};

/** Star word per committed rating (1–5). */
// The score words live in `feedbackVocabulary.ts` and are RE-EXPORTED here, not
// redefined. A rating persists as a bare integer, so its word is the only record
// of what a 4 meant — the definition is held under the lock in `me/labelLock.ts`,
// which needs the literal (`as const`) types this module's `Record<number, string>`
// would erase. One definition, two import sites. `UNDERSTOOD_WORDS` is no longer
// asked but stays locked in the vocabulary module: rows saved before 2026-09
// still hold those integers.
export { RATING_WORDS } from "./feedbackVocabulary";

// No line promises the therapist reads these answers: no therapist surface does
// yet. The privacy line is shared with the check-in and changes when that view ships.
export { privacyLine } from "../checkin/checkinCopy";

/** One written question: its own screen, optional, saved as `field`. */
export interface FeedbackQuestion {
  field: "helpful" | "didntLand" | "doDifferently";
  title: string;
  placeholder: string;
}

export const FEEDBACK_COPY = {
  continueLabel: "Continue",
  saveLabel: "Save Answers",
  backLabel: "Back",
  optionalHint: "Optional",

  rating: {
    title: "How was your last session?",
    helper: "Honest is more useful than kind.",
    emptyPrompt: "Tap a star",
  },

  /** Screens 2–4, in order. Every one is optional. */
  questions: [
    {
      field: "helpful",
      title: "What was helpful?",
      placeholder: "A moment, a question, a new way of seeing something…",
    },
    {
      field: "didntLand",
      title: "What didn't get enough time, or didn't land?",
      placeholder: "A topic you only touched on, or a suggestion that didn't fit you two…",
    },
    {
      field: "doDifferently",
      title: "What would you do differently next time?",
      placeholder: "Something to try, say, or bring up next time…",
    },
  ] satisfies readonly FeedbackQuestion[],

  saved: {
    title: "Thanks for reflecting on your session.",
    body: "Your answers will help shape your coaching between sessions.",
    homeLabel: BACK_TO_HOME,
  },
} as const;
